import { NgModule } from '@angular/core';
import { RouterModule, TitleStrategy } from '@angular/router';
import { environment } from '../environments/environment';
import { AfasFunctionsManagementComponent } from './afas-functions-management/afas-functions-management.component';
import { CanDeactivateForm } from './can-deactivate-form';
import { AppRoute } from './interfaces/app-route.interface';
import { RouteOption } from './interfaces/route-option.interface';
import { IsAuthenticated } from './is-authenticated';
import { IsNotAuthenticated } from './is-not-authenticated';
import { AiReportsChatsComponent } from './pages/ai-reports/ai-reports-chats/ai-reports-chats.component';
import { ArticleCatalogManagementComponent } from './pages/article-catalog-management/article-catalog-management.component';
import { AsphaltOrderListComponent } from './pages/asphalt-order-list/asphalt-order-list.component';
import { LinkLoginComponent } from './pages/auth/link-login/link-login.component';
import { MicrosoftCallbackComponent } from './pages/auth/microsoft-callback/microsoft-callback.component';
import { RequestRestorePasswordComponent } from './pages/auth/request-restore-password/request-restore-password.component';
import { RestorePasswordComponent } from './pages/auth/restore-password/restore-password.component';
import { CheckHoursComponent } from './pages/check-hours/check-hours.component';
import { ChecklistPdfComponent } from './pages/checklists/checklist-pdf/checklist-pdf.component';
import { DayCheckRealisationsComponent } from './pages/day-check-realisations/day-check-realisations.component';
import { EmployeePlanningComponent } from './pages/employee-planning/employee-planning.component';
import { HourRegistrationComponent } from './pages/hour-registration/hour-registration.component';
import { LeaveEditComponent } from './pages/leave/leave-edit/leave-edit.component';
import { LeaveComponent } from './pages/leave/leave.component';
import { LogoutComponent } from './pages/logout/logout.component';
import { MaterialsManagement } from './pages/materials-management/materials-management.component';
import { NotSendSettlementsComponent } from './pages/not-send-entities/not-send-settlements.component';
import { PlanningDetailPrintComponent } from './pages/planning-detail-print/planning-detail-print.component';
import { PlanningOverviewComponent } from './pages/planning-overview/planning-overview.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { ProjectDayReportComponent } from './pages/project-day-report/project-day-report.component';
import { ProjectExecutorPlanningComponent } from './pages/project-executor-planning/project-executor-planning.component';
import { ProjectOverviewComponent } from './pages/project-overview/project-overview.component';
import { ProjectWeekReportConfigComponent } from './pages/project-week-report-dialog/project-week-report-config.component';
import { ProjectsComponent } from './pages/projects/projects.component';
import { RightsManagementComponent } from './pages/rights-management/rights-management.component';
import { SettlementSendAllComponent } from './pages/settlement-send-all/settlement-send-all.component';
import { CanDeactivateSettlement } from './pages/settlement/can-deactivate-settlement';
import { SettlementComponent } from './pages/settlement/settlement.component';
import { SettlementsSendComponent } from './pages/settlements-send/settlements-send.component';
import { StaffingComponent } from './pages/staffing/staffing.component';
import { TransportPlanningComponent } from './pages/transport-planning/transport-planning.component';
import { LoginAsDirkUserComponent } from './pages/users/login-as-dirk-user/login-as-dirk-user.component';
import { LoginAsUserComponent } from './pages/users/login-as-user/login-as-user.component';
import { UserEditComponent } from './pages/users/user-edit/user-edit.component';
import { UserManagementComponent } from './pages/users/user-management/user-management.component';
import { VkmPlanningComponent } from './pages/vkm/vkm-planning/vkm-planning.component';
import { VkmDayPlanningComponent } from './pages/vkm/vkm-projects/vkm-day-planning.component';
import { VkmWorkordersComponent } from './pages/vkm/vkm-workorders/vkm-workorders.component';
import { WeekCheckRealisationsComponent } from './pages/week-check-realisations/week-check-realisations.component';
import { DayDetailComponent } from './pages/week-schedule/day-detail/day-detail.component';
import { WeekSchedulePlanningDetailComponent } from './pages/week-schedule/week-schedule-planning-detail/week-schedule-planning-detail.component';
import { WeekScheduleComponent } from './pages/week-schedule/week-schedule.component';
import { Functionalities } from './services/auth/functionalities';
import { CodaltTitleStrategy } from './services/codalt-title-strategy.service';
import { OrderdirectInvoiceDetailComponent } from './trackline/pages/orderdirect-invoice-detail/orderdirect-invoice-detail.component';
import { OrderdirectInvoicesComponent } from './trackline/pages/orderdirect-invoices/orderdirect-invoices.component';
import { OrderdirectPurchaseInvoiceDetailComponent } from './trackline/pages/orderdirect-purchase-invoice-detail/orderdirect-purchase-invoice-detail.component';
import { OrderdirectPurchaseInvoicesComponent } from './trackline/pages/orderdirect-purchase-invoices/orderdirect-purchase-invoices.component';
import { TracklineDayReportPrintComponent } from './trackline/pages/trackline-day-report-print/trackline-day-report-print.component';
import { TracklineInventarisationOverviewComponent } from './trackline/pages/trackline-inventarisation-overview/trackline-inventarisation-overview.component';
import { TracklineOrderFormComponent } from './trackline/pages/trackline-order-form/trackline-order-form.component';
import { BehoefteOverviewComponent } from './trackline/pages/trackline-planning/behoefte-overview/behoefte-overview.component';
import { TrackLineWeekComponent } from './trackline/pages/trackline-planning/track-line-week/track-line-week.component';
import { TracklineYearPlanningComponent } from './trackline/pages/trackline-year-planning/trackline-year-planning.component';
import { Utils } from './utils.class';

export const canDeactivateEmployeePlanning = (component: EmployeePlanningComponent) =>
    component.openChangesBackActionCheck();

export const routes: AppRoute[] = [
    { path: '', redirectTo: environment.rootPage, pathMatch: 'full' },
    environment.asphaltPlanning
        ? {
              path: 'asfaltplanning',
              component: PlanningOverviewComponent,
              title: 'Asfaltplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['planning', 'asfalt', 'overzicht'],
                  searchName: () => `Asfaltplanning overzicht`,
              },
          }
        : null,
    environment.asphaltPlanning
        ? {
              path: 'asfaltplanning/:date',
              component: PlanningOverviewComponent,
              title: 'Asfaltplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['planning', 'asfalt', 'overzicht'],
                  searchName: (props: RouteOption) =>
                      `Asfaltplanning ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
              },
          }
        : null,
    environment.asphaltPlanning
        ? {
              path: 'asfaltplanning/:date/:planningId',
              component: PlanningOverviewComponent,
              title: 'Asfaltplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
              },
          }
        : null,
    {
        path: 'profiel',
        component: ProfileComponent,
        title: 'Profiel',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            searchWords: ['account', 'gegevens', 'afas'],
            searchName: () => `Profiel`,
        },
    },
    { path: 'print/:date/:token', component: LinkLoginComponent },
    {
        path: 'print/:date',
        component: PlanningOverviewComponent,
        title: 'Asfaltplanning',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.PlanningOverview },
    },
    {
        path: 'print',
        component: PlanningOverviewComponent,
        title: 'Asfaltplanning',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.PlanningOverview },
    },
    {
        path: 'boekingsregels',
        component: SettlementsSendComponent,
        title: 'Boekingsregels versturen',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['boekingsregels', 'afas', 'verloning', 'nacalculatie'],
            searchName: () => `Boekingsregels versturen`,
        },
    },
    {
        path: 'boekingsregels/:date',
        component: SettlementsSendComponent,
        title: 'Boekingsregels versturen',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['boekingsregels', 'afas', 'verloning', 'nacalculatie'],
            searchName: (props: RouteOption) => `Boekingsregels ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'boekingsregels-versturen',
        component: SettlementSendAllComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['boekingsregels', 'versturen', 'alle', 'afas', 'nacalculatie'],
            searchName: () => `Alle boekingsregels versturen`,
        },
    },
    environment.transportPlanning
        ? {
              path: 'transport-planning',
              component: TransportPlanningComponent,
              title: 'Transportplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.Transportplanning,
                  searchWords: ['planning', 'transport', 'tp'],
                  searchName: () => `Transportplanning`,
              },
          }
        : null,
    environment.transportPlanning
        ? {
              path: 'transport-planning/:date',
              component: TransportPlanningComponent,
              title: 'Transportplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.Transportplanning,
                  searchWords: ['planning', 'transport'],
                  searchName: (props: RouteOption) =>
                      `Transportplanning ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
              },
          }
        : null,
    environment.employeePlanning
        ? {
              path: 'personeelsplanning',
              component: EmployeePlanningComponent,
              title: 'Personeelsplanning',
              canDeactivate: [canDeactivateEmployeePlanning],
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.EmployeePlanningRead,
                  searchWords: ['planning', 'personeel', 'week'],
                  searchName: () => `Personeelsplanning`,
              },
          }
        : null,
    environment.employeePlanning
        ? {
              path: 'personeelsplanning/:week/:year',
              component: EmployeePlanningComponent,
              title: 'Personeelsplanning',
              canDeactivate: [canDeactivateEmployeePlanning],
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.EmployeePlanningRead,
                  searchWords: ['planning', 'personeel'],
                  searchName: (props: RouteOption) => `Personeelsplanning week ${props.week} ${props.year}`,
              },
          }
        : null,
    environment.employeePlanning && environment.asphaltPlanning
        ? {
              path: 'asfaltploegen',
              component: EmployeePlanningComponent,
              title: 'Personeelsplanning',
              canDeactivate: [canDeactivateEmployeePlanning],
              canActivate: [IsAuthenticated],
              data: {
                  searchWords: ['asfaltploegen', 'ploegen', 'teams'],
                  searchName: () => `Asfaltploegen personeelsplanning`,
              },
          }
        : null,
    environment.employeePlanning && environment.asphaltPlanning
        ? {
              path: 'asfaltploegen/:period/:year',
              component: EmployeePlanningComponent,
              title: 'Personeelsplanning',
              canDeactivate: [canDeactivateEmployeePlanning],
              canActivate: [IsAuthenticated],
              data: {
                  searchWords: ['asfaltploegen', 'ploegen', 'teams'],
                  searchName: (props: RouteOption) =>
                      `Asfaltploegen personeelsplanning periode ${props.period} ${props.year}`,
              },
          }
        : null,
    {
        path: 'staffing',
        component: StaffingComponent,
        title: 'Bezetting',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.Staffing,
            searchWords: ['bezetting'],
            searchName: () => `Bezetting`,
        },
    },
    {
        path: 'dagcontrole',
        component: DayCheckRealisationsComponent,
        title: 'Dagcontrole',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CommonHourCheck,
            searchWords: ['dagcontrole', 'controle', 'uren'],
            searchName: () => `Dagcontrole`,
        },
    },
    {
        path: 'dagcontrole/:date',
        component: DayCheckRealisationsComponent,
        title: 'Dagcontrole',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CommonHourCheck,
            searchWords: ['dagcontrole', 'controle', 'uren'],
            searchName: (props: RouteOption) => `Dagcontrole ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'weekcontrole',
        component: WeekCheckRealisationsComponent,
        title: 'Weekcontrole',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CommonHourCheck,
            searchWords: ['weekcontrole', 'controle', 'uren'],
            searchName: () => `Weekcontrole`,
        },
    },
    {
        path: 'weekcontrole/:year/:week',
        component: WeekCheckRealisationsComponent,
        title: 'Weekcontrole',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CommonHourCheck,
            searchWords: ['weekcontrole', 'controle', 'uren'],
            searchName: (props: RouteOption) => `Weekcontrole week ${props.week} ${props.year}`,
        },
    },
    {
        path: 'planning-detail/:id',
        component: PlanningDetailPrintComponent,
        title: 'Planning afdrukken',
        data: {
            requiredFunctionality: Functionalities.PlanningOverview,
        },
    },
    {
        path: 'opdrachtformulier/:planningId',
        component: TracklineOrderFormComponent,
        title: 'Opdrachtformulier',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PlanningOverview,
        },
    },
    {
        path: 'dagrapport-afdruk/:planningId',
        component: TracklineDayReportPrintComponent,
        title: 'Dagrapport afdrukken',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PlanningOverview, Functionalities.DeclarationManagement],
        },
    },
    { path: 'login', component: LinkLoginComponent, canActivate: [IsNotAuthenticated] },
    {
        path: 'wachtwoord-vergeten',
        component: RequestRestorePasswordComponent,
        title: 'Wachtwoord vergeten',
        canActivate: [IsNotAuthenticated],
    },
    { path: 'restore-password/:token', component: RestorePasswordComponent, title: 'Wachtwoord herstellen' },
    {
        path: 'melding-indienen',
        loadChildren: () =>
            import('./pages/public-ticket-form/public-ticket-form.module').then(m => m.PublicTicketFormModule),
    },
    { path: 'asphalt-order-list/:token/:millId/:date', component: LinkLoginComponent },
    {
        path: 'asphalt-order-list/:millId/:date',
        component: AsphaltOrderListComponent,
        title: 'Bestellijst asfalt',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.AsphaltManagement },
    },
    {
        path: 'hours',
        component: HourRegistrationComponent,
        title: 'Urenregistratie',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            requiredFunctionality: [Functionalities.Hours, Functionalities.PeriodSettlementExecution],
            searchWords: ['urenregistratie', 'uren', 'werktijd'],
            searchName: () => `Urenregistratie`,
        },
    },
    {
        path: 'hours/:date',
        component: HourRegistrationComponent,
        title: 'Urenregistratie',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            requiredFunctionality: [Functionalities.Hours, Functionalities.PeriodSettlementExecution],
            searchWords: ['urenregistratie', 'uren', 'werktijd'],
            searchName: (props: RouteOption) => `Urenregistratie ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'hours/:date/:userId',
        component: HourRegistrationComponent,
        title: 'Urenregistratie',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            requiredFunctionality: [
                Functionalities.Hours,
                Functionalities.PeriodSettlementExecution,
                Functionalities.CommonHourCheck,
            ],
            searchWords: ['urenregistratie', 'werktijd'],
            fallbackToday: true,
            searchName: (props: RouteOption) => `Urenregistratie ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'dagrapport/:planningId',
        loadChildren: () => import('./pages/day-report/day-report.module').then(m => m.DayReportModule),
        canActivate: [IsAuthenticated],
    },
    {
        path: 'weekschedule',
        component: WeekScheduleComponent,
        title: 'Weekoverzicht',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.OwnHours },
    },
    {
        path: 'weekschedule/planning/:planningId',
        component: WeekSchedulePlanningDetailComponent,
        title: 'Planningdetail',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.OwnHours },
    },
    {
        path: 'weekschedule/:week/:year',
        component: WeekScheduleComponent,
        title: 'Weekoverzicht',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.OwnHours },
    },
    {
        path: 'weekschedule/:date',
        component: DayDetailComponent,
        title: 'Dagdetail',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.OwnHours },
    },
    {
        path: 'check-hours',
        component: CheckHoursComponent,
        title: 'Urencontrole',
        canActivate: [IsAuthenticated],
        canDeactivate: [],
        data: {
            requiredFunctionality: [
                Functionalities.HourCheck,
                Functionalities.CommonHourCheck,
                Functionalities.HourCheckRestricted,
            ],
            searchWords: ['urencontrole'],
            searchName: () => `Urencontrole`,
        },
    },
    {
        path: 'check-hours/:date',
        component: CheckHoursComponent,
        title: 'Urencontrole',
        canActivate: [IsAuthenticated],
        canDeactivate: [],
        data: {
            requiredFunctionality: [
                Functionalities.HourCheck,
                Functionalities.CommonHourCheck,
                Functionalities.HourCheckRestricted,
            ],
            searchWords: ['urencontrole'],
            searchName: (props: RouteOption) => `Urencontrole ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'settlements',
        component: NotSendSettlementsComponent,
        title: 'Afas nacalculatie',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['afas', 'nacalculatie', 'settlements', 'boekingsregels'],
            searchName: () => `Afas controle`,
        },
    },
    {
        path: 'settlements/:period/:year',
        component: NotSendSettlementsComponent,
        title: 'Afas nacalculatie',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['afas', 'nacalculatie', 'settlements', 'boekingsregels'],
            searchName: (props: RouteOption) => `Afas controle ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'settlements/:period/:year/:type',
        component: NotSendSettlementsComponent,
        title: 'Afas nacalculatie',
        canActivate: [IsAuthenticated],
        data: { requiredFunctionality: Functionalities.PeriodSettlementExecution },
    },
    {
        path: 'settlements/:period/:year/:type/:project',
        component: NotSendSettlementsComponent,
        title: 'Afas nacalculatie',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.PeriodSettlementExecution,
            searchWords: ['afas', 'nacalculatie', 'settlements', 'project'],
            searchName: (props: RouteOption) => `Afas nacalculatie project ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'periodeverrekening',
        component: SettlementComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PeriodSettlementExecution, Functionalities.ReadPeriodSettlement],
            searchWords: ['periodeverrekening', 'pv', 'verloning', 'loon'],
            searchName: () => `Periodeverrekening`,
        },
    },
    {
        path: 'periodeverrekening/:userId',
        component: SettlementComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PeriodSettlementExecution, Functionalities.ReadPeriodSettlement],
            searchWords: ['periodeverrekening', 'pv', 'verloning', 'loon'],
            searchName: () => `Periodeverrekening`,
        },
    },
    {
        path: 'periodeverrekening/:userId/:period/:year',
        component: SettlementComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PeriodSettlementExecution, Functionalities.ReadPeriodSettlement],
            searchWords: ['periodeverrekening', 'pv', 'verloning', 'loon'],
            searchName: (props: RouteOption) => `Periodeverrekening ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'boekingsregels/:userId',
        component: SettlementComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PeriodSettlementExecution, Functionalities.ReadPeriodSettlement],
            searchWords: ['afas', 'regels', 'nacalculatie', 'verloning'],
            searchName: () => `Boekingsregels`,
        },
    },
    {
        path: 'boekingsregels/:userId/:period/:year',
        component: SettlementComponent,
        title: 'Periode verrekening',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.PeriodSettlementExecution, Functionalities.ReadPeriodSettlement],
            searchWords: ['afas', 'regels', 'nacalculatie', 'verloning'],
            searchName: (props: RouteOption) => `Boekingsregels ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'project-dagrapport/:project/:date',
        component: ProjectDayReportComponent,
        title: 'Project dagrapport',
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['project', 'dagrapport', 'rapport'],
            searchName: (props: RouteOption) => `Project dagrapport ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'project-dagrapport/:project',
        component: ProjectDayReportComponent,
        title: 'Project dagrapport',
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['project', 'dagrapport', 'rapport'],
            searchName: () => `Project dagrapport`,
        },
    },
    {
        path: 'project-dagrapport',
        component: ProjectDayReportComponent,
        title: 'Project dagrapport',
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['project', 'dagrapport', 'rapport'],
            searchName: () => `Project dagrapport`,
        },
    },
    {
        path: 'project-rapport/:project/:period/:year/:onlyTotals/:noHours/:noComments/:noAsphalt/:noCars',
        component: ProjectWeekReportConfigComponent,
        title: 'Weekrapport',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            searchWords: ['project', 'weekrapport', 'rapport'],
            searchName: (props: RouteOption) => `Project weekrapport ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'project-rapport/:project/:period/:year',
        component: ProjectWeekReportConfigComponent,
        title: 'Weekrapport',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            searchWords: ['project', 'weekrapport', 'rapport'],
            searchName: (props: RouteOption) => `Project weekrapport ${props.year} - ${props.period}`,
        },
    },
    {
        path: 'project-rapport/:project',
        component: ProjectWeekReportConfigComponent,
        title: 'Weekrapport',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            searchWords: ['project', 'weekrapport', 'rapport'],
            searchName: () => `Project weekrapport`,
        },
    },
    {
        path: 'project-rapport',
        component: ProjectWeekReportConfigComponent,
        title: 'Weekrapport',
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['project', 'weekrapport', 'rapport'],
            searchName: () => `Project weekrapport`,
        },
    },
    {
        path: 'leave',
        component: LeaveComponent,
        title: 'Verlof',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.Leave, Functionalities.LeaveManagement],
            searchWords: ['verlof', 'vrij', 'ziek'],
            searchName: () => `Verlof`,
        },
    },
    {
        path: 'leave/:id',
        component: LeaveEditComponent,
        title: 'Verlof & verzuim',
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['verlof', 'vrij', 'bewerken'],
            searchName: () => `Verlof bewerken`,
        },
    },
    {
        path: 'projects-overview',
        component: ProjectOverviewComponent,
        title: 'Gecombineerde weekrapporten',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.ProjectManagement,
            searchWords: ['projecten', 'overzicht', 'weekrapporten', 'facturen', 'afas', 'factuur', 'gecombineerde'],
            searchName: () => `Gecombineerde weekrapporten`,
        },
    },
    {
        path: 'projects',
        component: ProjectsComponent,
        title: 'Projecten',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.ProjectManagement,
            searchWords: ['projecten', 'projectbeheer', 'afas'],
            searchName: () => `Projectenbeheer`,
        },
    },
    {
        path: 'projects/:project',
        component: ProjectsComponent,
        title: 'Projecten',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.ProjectManagement,
            searchWords: ['projecten', 'projectbeheer', 'afas'],
            searchName: () => `Projectenbeheer`,
        },
    },
    environment.vkmPlanning
        ? {
              path: 'vkm/:id',
              component: VkmPlanningComponent,
              title: 'Verkeersmaatregelen',
              canActivate: [IsAuthenticated],
              canDeactivate: [CanDeactivateForm],
              data: {
                  requiredFunctionality: [
                      Functionalities.VkmRead,
                      Functionalities.VkmReadWithPrice,
                      Functionalities.VkmWorkorderExecution,
                      Functionalities.VkmManagement,
                  ],
              },
          }
        : null,
    environment.vkmPlanning
        ? {
              path: 'vkm-werkorders',
              component: VkmWorkordersComponent,
              title: 'Verkeersmaatregelen',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: [
                      Functionalities.VkmRead,
                      Functionalities.VkmReadWithPrice,
                      Functionalities.VkmWorkorderExecution,
                      Functionalities.VkmManagement,
                  ],
                  searchWords: ['vkm', 'werkopdrachten', 'verkeersmaatregelen'],
                  searchName: () => `VKM werkopdrachten`,
              },
          }
        : null,
    environment.vkmPlanning
        ? {
              path: 'vkm-planning',
              component: VkmDayPlanningComponent,
              title: 'Planning DvdS - Verkeersmaatregelen',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: [
                      Functionalities.VkmRead,
                      Functionalities.VkmReadWithPrice,
                      Functionalities.VkmWorkorderExecution,
                      Functionalities.VkmManagement,
                  ],
                  searchWords: ['vkm', 'verkeersmaatregelen', 'planning'],
                  searchName: () => `VKM planning`,
              },
          }
        : null,
    environment.vkmPlanning
        ? {
              path: 'vkm-planning/:date',
              component: VkmDayPlanningComponent,
              title: 'Planning DvdS - Verkeersmaatregelen',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: [
                      Functionalities.VkmRead,
                      Functionalities.VkmReadWithPrice,
                      Functionalities.VkmWorkorderExecution,
                      Functionalities.VkmManagement,
                  ],
                  searchWords: ['verkeersmaatregelen', 'afzettingen'],
                  searchName: (props: RouteOption) =>
                      `Verkeersmaatregelen ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
              },
          }
        : null,
    {
        path: 'codalt/logs',
        loadChildren: () => import('./codalt/log-viewer/codalt-log-viewer.module').then(m => m.CodaltLogViewerModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltLogViewer,
            searchWords: ['logs', 'logbestanden', 'codalt'],
            searchName: () => `Logbestanden`,
        },
    },
    {
        path: 'codalt/logs/:date',
        loadChildren: () => import('./codalt/log-viewer/codalt-log-viewer.module').then(m => m.CodaltLogViewerModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltLogViewer,
            searchWords: ['logs', 'logbestanden', 'codalt'],
            searchName: (props: RouteOption) => `Logbestanden ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'codalt/failed-jobs',
        loadChildren: () =>
            import('./codalt/failed-jobs/codalt-failed-jobs.module').then(m => m.CodaltFailedJobsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltFailedJobs,
            searchWords: ['failed', 'jobs', 'queue', 'codalt'],
            searchName: () => `Failed Jobs`,
        },
    },
    {
        path: 'codalt/failed-jobs/:date',
        loadChildren: () =>
            import('./codalt/failed-jobs/codalt-failed-jobs.module').then(m => m.CodaltFailedJobsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltFailedJobs,
            searchWords: ['failed', 'jobs', 'queue', 'codalt'],
            searchName: (props: RouteOption) => `Failed Jobs ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'codalt/failed-jobs/:date/:id',
        loadChildren: () =>
            import('./codalt/failed-jobs/codalt-failed-jobs.module').then(m => m.CodaltFailedJobsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltFailedJobs,
        },
    },
    {
        path: 'codalt/apparaten',
        loadChildren: () =>
            import('./codalt/client-devices/codalt-client-devices.module').then(m => m.CodaltClientDevicesModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltMode,
        },
    },
    {
        path: 'codalt/app-logs',
        loadChildren: () => import('./codalt/app-logs/codalt-app-logs.module').then(m => m.CodaltAppLogsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltAppLogs,
            searchWords: ['app', 'logs', 'frontend', 'codalt'],
            searchName: () => `App Logs`,
        },
    },
    {
        path: 'codalt/app-logs/:date',
        loadChildren: () => import('./codalt/app-logs/codalt-app-logs.module').then(m => m.CodaltAppLogsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltAppLogs,
            searchWords: ['app', 'logs', 'frontend', 'codalt'],
            searchName: (props: RouteOption) => `App Logs ${Utils.formatDate(props.date, 'EEE d MMM yyyy')}`,
        },
    },
    {
        path: 'codalt/app-logs/:date/:id',
        loadChildren: () => import('./codalt/app-logs/codalt-app-logs.module').then(m => m.CodaltAppLogsModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.CodaltAppLogs,
        },
    },
    {
        path: 'tickets',
        loadChildren: () => import('./pages/tickets/tickets.module').then(m => m.TicketsModule),
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['tickets', 'meldingen', 'problemen', 'kam'],
            searchName: () => `Meldingen`,
        },
    },
    {
        path: 'checklists',
        loadChildren: () => import('./pages/checklists/checklists.module').then(m => m.ChecklistsModule),
        canActivate: [IsAuthenticated],
        data: {
            searchWords: ['checklists', 'inspecties', 'controles', 'kam'],
            searchName: () => `Inspecties`,
        },
    },
    {
        path: 'checklists/pdf/:completedId',
        component: ChecklistPdfComponent,
        title: 'Checklist PDF',
        canActivate: [IsNotAuthenticated],
    },
    {
        path: 'opdrachtgevers',
        loadChildren: () =>
            import('./pages/customers-management/customers-management.module').then(m => m.CustomersManagementModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.ClientManagement,
            searchWords: ['opdrachtgevers', 'klanten', 'klantgebieden'],
            searchName: () => `Opdrachtgeversbeheer`,
        },
    },
    {
        path: 'rechtenmatrix',
        component: RightsManagementComponent,
        title: 'Rechtenmatrix',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.RightsMatrix,
            searchWords: ['rechtenmatrix', 'rechten', 'functionaliteiten'],
            searchName: () => `Rechtenmatrix`,
        },
    },
    {
        path: 'crm-functies',
        component: AfasFunctionsManagementComponent,
        title: 'CRM functies',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.RightsMatrix,
            searchWords: ['crm', 'functies', 'rollen', 'koppeling'],
            searchName: () => `CRM Functies`,
        },
    },
    { path: 'logout', component: LogoutComponent },
    environment.executorPlanning
        ? {
              path: 'project-executor-planning',
              component: ProjectExecutorPlanningComponent,
              title: 'Uitvoerdersplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: [Functionalities.ExecutorPlanning, Functionalities.ExecutorPlanningRead],
                  searchWords: ['uitvoerdersplanning'],
                  searchName: () => `Uitvoerdersplanning`,
              },
          }
        : null,
    {
        path: 'gebruikers',
        component: UserManagementComponent,
        title: 'Gebruikers',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.UserManagement, Functionalities.UserManagementExternal],
            searchWords: ['gebruikers'],
            searchName: () => `Gebruikersbeheer`,
        },
    },
    environment.orderDirect
        ? {
              path: 'orderdirect-facturen',
              component: OrderdirectInvoicesComponent,
              title: 'OrderDirect facturen',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.UserManagement,
                  searchWords: ['orderdirect', 'facturen', 'invoices'],
                  searchName: () => `OrderDirect Facturen`,
              },
          }
        : null,
    environment.orderDirect
        ? {
              path: 'orderdirect-facturen/:id',
              component: OrderdirectInvoiceDetailComponent,
              title: 'OrderDirect Factuur Detail',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.UserManagement,
                  searchWords: ['orderdirect', 'facturen', 'invoices', 'detail'],
                  searchName: () => `OrderDirect Factuur Detail`,
              },
          }
        : null,
    environment.orderDirect
        ? {
              path: 'orderdirect-inkoopfacturen',
              component: OrderdirectPurchaseInvoicesComponent,
              title: 'OrderDirect inkoopfacturen',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.UserManagement,
                  searchWords: ['orderdirect', 'inkoop', 'facturen', 'purchase', 'invoices'],
                  searchName: () => `OrderDirect Inkoopfacturen`,
              },
          }
        : null,
    environment.orderDirect
        ? {
              path: 'orderdirect-inkoopfacturen/:id',
              component: OrderdirectPurchaseInvoiceDetailComponent,
              title: 'OrderDirect Inkoopfactuur Detail',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.UserManagement,
                  searchWords: ['orderdirect', 'inkoop', 'facturen', 'purchase', 'invoices', 'detail'],
                  searchName: () => `OrderDirect Inkoopfactuur Detail`,
              },
          }
        : null,
    {
        path: 'gebruikers/login-as/:userId',
        component: LoginAsUserComponent,
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.UserTakeover,
            searchWords: ['gebruikers', 'inloggen als', 'overnemen'],
            sortName: (props: RouteOption) => (props.user?.deleted_at ? 'xxxxxxx' : 'aaaaaaa'),
            searchName: () => `Inloggen als gebruiker`,
        },
    },
    {
        path: 'gebruikers/login-as-dirk/:userId',
        component: LoginAsDirkUserComponent,
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.DirkUserTakeover,
            searchWords: ['gebruikers', 'inloggen als', 'overnemen', 'dirk'],
            sortName: (props: RouteOption) => (props.user?.deleted_at ? 'xxxxxxx' : 'aaaaaaa'),
            searchName: () => `Inloggen als Dirk-gebruiker`,
        },
    },
    {
        path: 'gebruikers/:userId',
        component: UserEditComponent,
        title: 'Gebruiker',
        canActivate: [IsAuthenticated],
        canDeactivate: [CanDeactivateForm],
        data: {
            requiredFunctionality: [Functionalities.UserManagement, Functionalities.UserManagementExternal],
            searchWords: ['gebruikersbeheer', 'reistijd', 'overuren', 'locatie', 'auto', 'rechten', 'rollen', 'afas'],
            searchName: () => `Gebruikersbeheer`,
            sortName: (props: RouteOption) => (props.user?.deleted_at ? 'xxxxxxx' : 'bbbbbbb'),
            allUsers: true,
        },
    },
    {
        path: 'ai/rapporten',
        component: AiReportsChatsComponent,
        title: 'Rapporten',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.AiReports,
            searchWords: ['AI Rapporten', 'ai', 'rapporten'],
            searchName: () => `AI Rapporten`,
        },
    },
    {
        path: 'ai/rapporten/:chatId',
        component: AiReportsChatsComponent,
        title: 'Rapporten',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.AiReports,
            searchWords: ['AI Rapporten', 'ai', 'rapporten'],
            searchName: () => `AI Rapporten`,
        },
    },
    {
        path: 'inloggen/entra',
        component: MicrosoftCallbackComponent,
    },
    environment.weekPlanning
        ? {
              path: 'weekplanning',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: TrackLineWeekComponent,
              title: 'Weekplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['planning', 'weekplanning', 'overzicht'],
                  searchName: () => `Weekplanning`,
              },
          }
        : null,
    environment.weekPlanning
        ? {
              path: 'behoefte',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: BehoefteOverviewComponent,
              title: 'Behoefte',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['behoefte', 'markeren', 'thermoplast', 'verf', 'koudplast'],
                  searchName: () => `Behoefte`,
              },
          }
        : null,
    environment.weekPlanning
        ? {
              path: 'weekplanning/behoefte/:planningId',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: TrackLineWeekComponent,
              title: 'Weekplanning',
              canActivate: [IsAuthenticated],
              data: { requiredFunctionality: Functionalities.PlanningOverview },
          }
        : null,
    environment.weekPlanning
        ? {
              path: 'weekplanning/:week/:year',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: TrackLineWeekComponent,
              title: 'Weekplanning',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['planning', 'weekplanning', 'overzicht'],
                  searchName: (props: RouteOption) => `Weekplanning week ${props.week} ${props.year}`,
              },
          }
        : null,
    environment.weekPlanning
        ? {
              path: 'weekplanning/:week/:year/:planningId',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: TrackLineWeekComponent,
              title: 'Weekplanning',
              canActivate: [IsAuthenticated],
              data: { requiredFunctionality: Functionalities.PlanningOverview },
          }
        : null,
    environment.weekPlanning
        ? {
              path: 'jaarplanning',
              component: TracklineYearPlanningComponent,
              title: 'Jaarplanning',
              canActivate: [IsAuthenticated],
              data: {
                  searchWords: ['planning', 'jaarplanning', 'overzicht', 'jaar'],
                  searchName: () => `Jaarplanning`,
              },
          }
        : null,
    environment.inventarizePlanning
        ? {
              path: 'inventarisatie',
              loadChildren: () =>
                  import('./trackline/pages/trackline-planning/trackline-planning.module').then(
                      m => m.TracklinePlanningModule
                  ),
              component: TracklineInventarisationOverviewComponent,
              title: 'Inventarisatie overzicht',
              canActivate: [IsAuthenticated],
              data: {
                  requiredFunctionality: Functionalities.PlanningOverview,
                  searchWords: ['planning', 'inventarisatie', 'overzicht'],
                  searchName: () => `Inventarisatie`,
              },
          }
        : null,
    {
        path: 'materiaalbeheer',
        component: MaterialsManagement,
        title: 'Materiaalbeheer',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.MaterialsManagement,
        },
    },
    {
        path: 'artikelenbeheer',
        component: ArticleCatalogManagementComponent,
        title: 'Artikelenbeheer',
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: Functionalities.ArticleCatalogManagement,
        },
    },
    {
        path: 'bestekprijzen',
        loadChildren: () =>
            import('./pages/article-management/article-management.module').then(m => m.ArticleManagementModule),
        canActivate: [IsAuthenticated],
        data: {
            requiredFunctionality: [Functionalities.ArticleManagement, Functionalities.DeclarationManagement],
        },
    },
].filter(Boolean) as AppRoute[];

@NgModule({
    imports: [RouterModule.forRoot(routes, {})],
    exports: [RouterModule],
    providers: [
        IsAuthenticated,
        IsNotAuthenticated,
        CanDeactivateSettlement,
        { provide: TitleStrategy, useClass: CodaltTitleStrategy },
    ],
})
export class AppRoutingModule {}
